import { FunctionComponent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faBuilding,
	faCircleMinus,
	faCopy,
	faPencil,
	faTrash,
	faTriangleExclamation,
	faUser,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import {
	readSizeTiers,
	unassignedSizeBuckets,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/sizeBuckets";

import { ScoreCriteriaType } from "../../schema/score.schema";
import { DetailHeader } from "../../shell/DetailHeader";
import { ScoringPickerMenu } from "../../shell/ScoreMatrix/ScoringPickerMenu";
import { criterionIssues, ScoreIssue } from "../../shell/scoreIssues";
import { AddChip, ChipStatus, StatusChip } from "../../shell/StatusChip";
import { useFlashOnFocus } from "../../shell/useFocusTarget";

import { CopyScoresMenu } from "./CopyScoresMenu";
import {
	addAiCriterion,
	aiCriterionOf,
	copyAllFrom,
	copyCriterionFrom,
	CriterionRef,
	criterionRefKey,
	isCriterionOn,
	listCriteria,
	removeAiCriterion,
	Section,
	setScoringActive,
} from "./criteriaDraft";
import { CriterionEditor } from "./CriterionEditor";

interface ScoringCardProps {
	scoring: ScoreCriteriaType;
	others: ScoreCriteriaType[];
	/** How many active combinations point at this scoring, pending picks included. */
	applied: number;
	flash: boolean;
	onFlashDone: () => void;
	onChange: (next: ScoreCriteriaType) => void;
	onDuplicate: () => void;
	onDelete: () => void;
	selectedCriterion: CriterionRef | null;
	onSelectCriterion: (ref: CriterionRef) => void;
}

const ACTION =
	"flex h-6 cursor-pointer items-center gap-1 rounded-md border px-2 text-[11px] font-medium";

/** What the chips and the editor call a criterion. */
export const useCriterionLabel = (scoring: ScoreCriteriaType) => {
	const { t } = useTranslation();

	return (ref: CriterionRef): string =>
		ref.key === "OTHER"
			? (aiCriterionOf(scoring, ref)?.criteriaName ?? "")
			: t(`leadScoring.scoringPage.criteria.${ref.key}`);
};

/** Whether an issue of the scoring is about the given criterion. */
const issueNames = (issue: ScoreIssue, scoring: ScoreCriteriaType, ref: CriterionRef): boolean => {
	const criterion = issue.message.params?.criterion;

	return ref.key === "OTHER"
		? criterion === aiCriterionOf(scoring, ref)?.criteriaName
		: criterion === ref.key;
};

/**
 * The card for the selected lead scoring (PREC-4055): activate toggle, name,
 * copy-from / duplicate / delete, a banner for what stops it from grading,
 * the criterion chips, and the editor of the selected criterion.
 */
export const ScoringCard: FunctionComponent<ScoringCardProps> = ({
	scoring,
	others,
	applied,
	flash,
	onFlashDone,
	onChange,
	onDuplicate,
	onDelete,
	selectedCriterion,
	onSelectCriterion,
}) => {
	const { t } = useTranslation();
	const cardRef = useRef<HTMLDivElement>(null);
	const copyButtonRef = useRef<HTMLButtonElement>(null);
	const addButtonRef = useRef<HTMLButtonElement>(null);
	const [renameSignal, setRenameSignal] = useState(0);
	const [copyOpen, setCopyOpen] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const labelOf = useCriterionLabel(scoring);

	useFlashOnFocus(cardRef, flash, onFlashDone);

	const criteria = useMemo(() => listCriteria(scoring), [scoring]);
	const issues = useMemo(() => criterionIssues(scoring), [scoring]);
	const selected =
		criteria.find(
			(ref) => selectedCriterion && criterionRefKey(ref) === criterionRefKey(selectedCriterion)
		) ?? criteria[0];

	const missing = criteria.filter((ref) => issues.some((issue) => issueNames(issue, scoring, ref)));

	// PREC-4127: a size range in no tier is legal, so it is not an issue and must
	// not read as one — but the chip said "complete" with a green check, which is
	// why the editor's warning was never opened. Amber, and below "bad".
	const sizeGap = unassignedSizeBuckets(readSizeTiers(scoring)).length > 0;

	const statusOf = (ref: CriterionRef): ChipStatus => {
		if (!isCriterionOn(scoring, ref)) {
			return "off";
		}

		if (missing.includes(ref)) {
			return "bad";
		}

		return ref.key === "EMPLOYEE_SIZE" && sizeGap ? "warn" : "ok";
	};

	const addAi = (section: Section) => {
		const { scoring: next, ref } = addAiCriterion(
			scoring,
			section,
			t("leadScoring.scoringPage.aiCriterionName")
		);

		onChange(next);
		onSelectCriterion(ref);
	};

	const banner = () => {
		if (!scoring.locked) {
			return (
				<div
					className="mb-3 flex items-start gap-2 rounded-lg border border-tag-warning-border bg-background-warning px-3 py-2 text-xs text-tag-warning-dark"
					data-testid="scoring-banner-off"
				>
					<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faCircleMinus} />
					{t("leadScoring.scoringPage.deactivatedBanner")}
				</div>
			);
		}

		if (missing.length > 0) {
			return (
				<div
					className="mb-3 flex items-start gap-2 rounded-lg border border-status-error-light bg-error-light px-3 py-2 text-xs font-semibold text-status-error"
					data-testid="scoring-banner-missing"
				>
					<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
					{t("leadScoring.scoringPage.missingBanner", {
						name: scoring.name,
						criteria: missing.map(labelOf).join(", "),
					})}
				</div>
			);
		}

		if (applied === 0) {
			return (
				<div
					className="mb-3 flex items-start gap-2 rounded-lg border border-tag-warning-border bg-background-warning px-3 py-2 text-xs text-tag-warning-dark"
					data-testid="scoring-banner-unused"
				>
					<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
					{t("leadScoring.scoringPage.notAppliedBanner")}
				</div>
			);
		}

		return null;
	};

	return (
		<div
			ref={cardRef}
			className="overflow-hidden rounded-xl border border-border-light bg-white shadow-sm"
			data-testid={`scoring-card-${scoring.id}`}
		>
			<DetailHeader
				active={scoring.locked}
				activeTitle={t("leadScoring.scoringPage.scoringActive")}
				inactiveTitle={t("leadScoring.scoringPage.scoringInactive")}
				name={scoring.name}
				renameSignal={renameSignal}
				testId="scoring-detail"
				actions={
					<div className="ml-auto flex flex-wrap items-center gap-1.5">
						<button
							className={classNames(
								ACTION,
								"border-transparent text-text-meta hover:bg-background-light"
							)}
							type="button"
							onClick={() => setRenameSignal((n) => n + 1)}
						>
							<Icon className="h-3 w-3" icon={faPencil} />
							{t("leadScoring.detail.rename")}
						</button>
						<button
							ref={copyButtonRef}
							className={classNames(
								ACTION,
								"border-border-light bg-white text-text hover:bg-background-light"
							)}
							data-testid="scoring-copy-from"
							type="button"
							onClick={() => setCopyOpen((current) => !current)}
						>
							<Icon className="h-3 w-3" icon={faCopy} />
							{t("leadScoring.scoringPage.copyFrom")}
						</button>
						<button
							className={classNames(
								ACTION,
								"border-border-light bg-white text-text hover:bg-background-light"
							)}
							data-testid="scoring-duplicate"
							type="button"
							onClick={onDuplicate}
						>
							<Icon className="h-3 w-3" icon={faCopy} />
							{t("leadScoring.scoringPage.duplicate")}
						</button>
						<button
							className={classNames(
								ACTION,
								"border-status-error-light text-status-error hover:bg-error-light"
							)}
							data-testid="scoring-delete"
							type="button"
							onClick={onDelete}
						>
							<Icon className="h-3 w-3" icon={faTrash} />
							{t("leadScoring.detail.deletePermanently")}
						</button>
					</div>
				}
				onRename={(name) => onChange({ ...scoring, name })}
				onToggle={(active) => onChange(setScoringActive(scoring, active))}
			/>

			<div className="px-4 py-3.5">
				{banner()}

				<div className="flex flex-wrap items-center gap-2" data-testid="criterion-chips">
					{criteria.map((ref) => (
						<StatusChip
							key={criterionRefKey(ref)}
							label={labelOf(ref) || t("leadScoring.shell.untitled")}
							selected={criterionRefKey(ref) === criterionRefKey(selected)}
							status={statusOf(ref)}
							testId={`criterion-chip-${criterionRefKey(ref)}`}
							onClick={() => onSelectCriterion(ref)}
						/>
					))}
					<span ref={addButtonRef} title={t("leadScoring.scoringPage.addAiCriterionTip")}>
						<AddChip
							label={t("leadScoring.scoringPage.addAiCriterion")}
							testId="add-ai-criterion"
							onClick={() => setAddOpen((current) => !current)}
						/>
					</span>
					{addOpen && addButtonRef.current && (
						<ScoringPickerMenu
							anchorEl={addButtonRef.current}
							items={[
								{
									icon: faBuilding,
									label: t("leadScoring.scoringPage.addAiCompany"),
									onClick: () => addAi("companyScoring"),
								},
								{
									icon: faUser,
									label: t("leadScoring.scoringPage.addAiPerson"),
									onClick: () => addAi("personaScoring"),
								},
							]}
							onClose={() => setAddOpen(false)}
						/>
					)}
				</div>

				{selected && (
					<CriterionEditor
						key={`${scoring.id}-${criterionRefKey(selected)}`}
						criterionRef={selected}
						label={labelOf(selected)}
						scoring={scoring}
						onChange={onChange}
						onRemove={
							selected.key === "OTHER"
								? () => {
										onChange(removeAiCriterion(scoring, selected));
										onSelectCriterion(criteria[0]);
									}
								: undefined
						}
					/>
				)}
			</div>

			{copyOpen && copyButtonRef.current && (
				<CopyScoresMenu
					anchorEl={copyButtonRef.current}
					criterionLabel={labelOf(selected)}
					criterionRef={selected}
					others={others}
					scoring={scoring}
					onClose={() => setCopyOpen(false)}
					onCopyAll={(source) => onChange(copyAllFrom(scoring, source))}
					onCopyCriterion={(source) => onChange(copyCriterionFrom(scoring, source, selected))}
				/>
			)}
		</div>
	);
};
